import { Slot } from '@radix-ui/react-slot';
import React from 'react';

import {
  SIZE_SCALE,
  ANIMATIONS,
  RADIUS_SCALE,
  OPACITY_SCALE,
  DURATION_SCALE as _DURATION_SCALE,
  TRANSITION,
} from '@/components/design-system/tokens';
import { cn } from '@/lib/utils';

import { buttonVariants } from './buttonVariants';

function getRadiusClass(radius?: 'none' | 'sm' | 'md' | 'lg' | 'xl' | 'full'): string {
  switch (radius) {
    case 'none':
      return RADIUS_SCALE.none;
    case 'sm':
      return RADIUS_SCALE.sm;
    case 'md':
      return RADIUS_SCALE.md;
    case 'lg':
      return RADIUS_SCALE.lg;
    case 'xl':
      return RADIUS_SCALE.xl;
    case 'full':
      return RADIUS_SCALE.full;
    default:
      return RADIUS_SCALE.md;
  }
}

interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'size'> {
  variant?:
    | 'primary'
    | 'secondary'
    | 'outline'
    | 'ghost'
    | 'link'
    | 'destructive'
    | 'success'
    | 'warning'
    | 'info'
    | 'pagination'
    | 'paginationActive'
    | 'elevated'
    | 'tertiary'
    | 'oauth'
    | 'ai'
    | 'danger'
    | 'default';
  size?: 'sm' | 'md' | 'lg' | 'default' | 'icon';
  radius?: 'none' | 'sm' | 'md' | 'lg' | 'xl' | 'full';
  asChild?: boolean;
  render?: React.ComponentType<React.HTMLAttributes<HTMLElement>>;
  showArrow?: boolean;
  pointLeft?: boolean;
  pointExternal?: boolean;
  loading?: boolean;
  fullWidth?: boolean;
  children?: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      radius = 'md',
      asChild = false,
      render,
      showArrow = false,
      pointLeft = false,
      pointExternal = false,
      loading = false,
      className,
      children,
      disabled = false,
      onClick,
      type = 'button' as const,
      fullWidth = false,
      ...props
    },
    ref
  ) => {
    const arrowIcon = (
      <svg
        className={cn(SIZE_SCALE.w4, TRANSITION.transform, pointLeft && 'rotate-180')}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    );

    const externalIcon = (
      <svg className={SIZE_SCALE.w4} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
        />
      </svg>
    );

    const loadingSpinner = (
      <svg className={`${ANIMATIONS.spin} ${SIZE_SCALE.w4}`} fill="none" viewBox="0 0 24 24">
        <circle
          className={OPACITY_SCALE[10]}
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className={OPACITY_SCALE[70]}
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    );

    const buttonContent = (
      <>
        {loading && loadingSpinner}
        {children}
        {showArrow && !pointExternal && arrowIcon}
        {pointExternal && externalIcon}
      </>
    );

    const Comp = asChild ? Slot : 'button';

    const buttonElement = (
      <Comp
        ref={ref}
        type={!asChild ? type : undefined}
        className={cn(
          buttonVariants({ variant, size }),
          getRadiusClass(radius),
          fullWidth && SIZE_SCALE.full,
          className
        )}
        disabled={!asChild ? disabled || loading : undefined}
        onClick={!asChild ? onClick : undefined}
        {...props}
      >
        {buttonContent}
      </Comp>
    );

    // If render prop is provided, use it to wrap the button
    if (render) {
      const RenderComponent = render;
      return <RenderComponent {...props}>{buttonContent}</RenderComponent>;
    }

    return buttonElement;
  }
);

Button.displayName = 'Button';

export { Button };
